import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
} from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useOrganization } from "../organization/OrganizationProvider";

type ServiceOrderStatus =
  | "received"
  | "inspection"
  | "diagnosing"
  | "awaiting_approval"
  | "approved"
  | "in_progress"
  | "testing"
  | "ready"
  | "delivered"
  | "cancelled";

type ServiceOrderRow = {
  id: string;
  order_number: string;
  status: ServiceOrderStatus;
  priority: string;
  customer_id: string;
  customer_name: string;
  quotation_id: string | null;
  quotation_number: string | null;
  customer_report: string | null;
  requested_service: string | null;
  estimated_total: number | string;
  approved_total: number | string;
  final_total: number | string;
  received_at: string;
  ready_at: string | null;
  delivered_at: string | null;
  updated_at: string;
};

type CustomerOption = {
  id: string;
  name: string;
  document_type: string | null;
  document_number: string | null;
  status: string;
};

type CandidateRow = {
  quotation_id: string;
  quotation_number: string;
  customer_id: string;
  customer_name: string;
  total: number | string;
  currency_code: string;
  approved_at: string | null;
};

type ServiceOrderAction = {
  value: ServiceOrderStatus;
  label: string;
  danger?: boolean;
};

const STATUS_LABELS: Record<ServiceOrderStatus, string> = {
  received: "Recibida",
  inspection: "Inspección",
  diagnosing: "Diagnóstico",
  awaiting_approval: "Esperando aprobación",
  approved: "Aprobada",
  in_progress: "En servicio",
  testing: "Pruebas",
  ready: "Terminada",
  delivered: "Entregada",
  cancelled: "Cancelada",
};

function statusActions(status: ServiceOrderStatus): ServiceOrderAction[] {
  switch (status) {
    case "received":
      return [
        { value: "inspection" as const, label: "Iniciar inspección" },
        { value: "diagnosing" as const, label: "Diagnosticar" },
        { value: "cancelled" as const, label: "Cancelar", danger: true },
      ];
    case "inspection":
      return [
        { value: "diagnosing" as const, label: "Diagnosticar" },
        { value: "cancelled" as const, label: "Cancelar", danger: true },
      ];
    case "diagnosing":
      return [
        { value: "awaiting_approval" as const, label: "Solicitar aprobación" },
        { value: "cancelled" as const, label: "Cancelar", danger: true },
      ];
    case "awaiting_approval":
      return [
        { value: "approved" as const, label: "Aprobar servicio" },
        { value: "cancelled" as const, label: "Cancelar", danger: true },
      ];
    case "approved":
      return [
        { value: "in_progress" as const, label: "Iniciar servicio" },
        { value: "cancelled" as const, label: "Cancelar", danger: true },
      ];
    case "in_progress":
      return [
        { value: "testing" as const, label: "Pasar a pruebas" },
        { value: "ready" as const, label: "Marcar terminada" },
        { value: "cancelled" as const, label: "Cancelar", danger: true },
      ];
    case "testing":
      return [
        { value: "in_progress" as const, label: "Volver a servicio" },
        { value: "ready" as const, label: "Marcar terminada" },
        { value: "cancelled" as const, label: "Cancelar", danger: true },
      ];
    case "ready":
      return [{ value: "delivered" as const, label: "Marcar entregada" }];
    default:
      return [];
  }
}

function money(value: number | string, currency = "PEN") {
  return new Intl.NumberFormat("es-PE", {
    style: "currency",
    currency,
  }).format(Number(value || 0));
}

export function WorkshopPage() {
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.organizationId ?? null;

  const [orders, setOrders] = useState<ServiceOrderRow[]>([]);
  const [candidates, setCandidates] = useState<CandidateRow[]>([]);
  const [customers, setCustomers] = useState<CustomerOption[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualBusy, setManualBusy] = useState(false);
  const [manualCustomerId, setManualCustomerId] = useState("");
  const [manualPriority, setManualPriority] = useState("normal");
  const [manualReport, setManualReport] = useState("");
  const [manualService, setManualService] = useState("");
  const [manualCondition, setManualCondition] = useState("");
  const [manualAccessories, setManualAccessories] = useState("");
  const [manualNotes, setManualNotes] = useState("");

  const loadData = useCallback(
    async (query: string) => {
      if (!organizationId) {
        setOrders([]);
        setCandidates([]);
        setCustomers([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setLoadError(null);

      const [ordersResult, candidatesResult, customersResult] =
        await Promise.all([
          supabase.rpc("list_organization_service_orders", {
            p_organization_id: organizationId,
            p_search: query.trim() || null,
          }),
          supabase.rpc("list_service_order_candidates", {
            p_organization_id: organizationId,
          }),
          supabase.rpc("list_organization_customers", {
            p_organization_id: organizationId,
            p_search: null,
          }),
        ]);

      if (
        ordersResult.error ||
        candidatesResult.error ||
        customersResult.error
      ) {
        setLoadError(
          ordersResult.error?.message ??
            candidatesResult.error?.message ??
            customersResult.error?.message ??
            "No se pudo cargar Taller.",
        );
      }

      setOrders(
        ordersResult.error ? [] : ((ordersResult.data ?? []) as ServiceOrderRow[]),
      );
      setCandidates(
        candidatesResult.error
          ? []
          : ((candidatesResult.data ?? []) as CandidateRow[]),
      );
      setCustomers(
        customersResult.error
          ? []
          : ((customersResult.data ?? []) as CustomerOption[]).filter(
              (customer) => customer.status === "active",
            ),
      );
      setLoading(false);
    },
    [organizationId],
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void loadData(search);
    }, 250);

    return () => window.clearTimeout(timer);
  }, [loadData, search]);

  useEffect(() => {
    setSearch("");
    setActionError(null);
    setActionSuccess(null);
    setBusyId(null);
    setManualOpen(false);
    setManualCustomerId("");
    setManualPriority("normal");
    setManualReport("");
    setManualService("");
    setManualCondition("");
    setManualAccessories("");
    setManualNotes("");
  }, [organizationId]);

  const openOrders = useMemo(
    () =>
      orders.filter(
        (order) =>
          order.status !== "delivered" && order.status !== "cancelled",
      ).length,
    [orders],
  );

  async function createManualOrder(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!organizationId || !manualCustomerId) {
      setActionError("Selecciona un cliente.");
      return;
    }

    if (!manualReport.trim() && !manualService.trim()) {
      setActionError("Ingresa el motivo de ingreso o el servicio solicitado.");
      return;
    }

    setManualBusy(true);
    setActionError(null);
    setActionSuccess(null);

    const { data, error } = await supabase.rpc(
      "create_manual_service_order",
      {
        p_organization_id: organizationId,
        p_customer_id: manualCustomerId,
        p_asset_id: null,
        p_priority: manualPriority,
        p_customer_report: manualReport.trim() || null,
        p_customer_requested_service: manualService.trim() || null,
        p_initial_condition: manualCondition.trim() || null,
        p_accessories_received: manualAccessories.trim() || null,
        p_intake_notes: manualNotes.trim() || null,
      },
    );

    setManualBusy(false);

    if (error) {
      setActionError(error.message);
      return;
    }

    setManualOpen(false);
    setManualCustomerId("");
    setManualPriority("normal");
    setManualReport("");
    setManualService("");
    setManualCondition("");
    setManualAccessories("");
    setManualNotes("");
    setActionSuccess(
      data ? "Orden de servicio manual creada correctamente." : "Orden creada.",
    );
    await loadData(search);
  }

  async function createOrder(candidate: CandidateRow) {
    setBusyId(candidate.quotation_id);
    setActionError(null);
    setActionSuccess(null);

    const { error } = await supabase.rpc(
      "create_service_order_from_quotation",
      {
        p_quotation_id: candidate.quotation_id,
        p_asset_id: null,
        p_priority: "normal",
        p_customer_report: null,
      },
    );

    setBusyId(null);

    if (error) {
      setActionError(error.message);
      return;
    }

    setActionSuccess(
      `Orden creada desde ${candidate.quotation_number}.`,
    );
    await loadData(search);
  }

  async function changeStatus(
    orderId: string,
    nextStatus: ServiceOrderStatus,
  ) {
    setBusyId(orderId);
    setActionError(null);
    setActionSuccess(null);

    const { error } = await supabase.rpc(
      "transition_service_order_status",
      {
        p_service_order_id: orderId,
        p_new_status: nextStatus,
        p_note: null,
      },
    );

    setBusyId(null);

    if (error) {
      setActionError(error.message);
      return;
    }

    await loadData(search);
  }

  if (!selectedOrganization) {
    return (
      <div className="page">
        <section className="empty-state">
          <strong>Selecciona una empresa.</strong>
          <p>Necesitas una empresa activa para trabajar con Taller.</p>
        </section>
      </div>
    );
  }

  return (
    <div className="page">
      <header className="page-header page-header-actions">
        <div>
          <span className="eyebrow">Taller</span>
          <h1>{selectedOrganization.name}</h1>
          <p>Órdenes de servicio, avance operativo y entrega.</p>
        </div>
        <button
          className="primary-button"
          type="button"
          onClick={() => {
            setManualOpen((value) => !value);
            setActionError(null);
            setActionSuccess(null);
          }}
        >
          {manualOpen ? "Cerrar" : "Nueva orden manual"}
        </button>
      </header>

      {manualOpen ? (
        <section className="panel-card">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Ingreso directo</span>
              <h2>Nueva orden de servicio manual</h2>
              <p>No requiere cotización ni orden de compra previa.</p>
            </div>
          </div>

          <form className="workshop-manual-form" onSubmit={createManualOrder}>
            <label className="workshop-manual-span-2">
              Cliente
              <select
                required
                value={manualCustomerId}
                onChange={(event) => setManualCustomerId(event.target.value)}
              >
                <option value="">Seleccionar cliente</option>
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name}
                    {customer.document_number
                      ? ` · ${customer.document_type ?? ""} ${customer.document_number}`
                      : ""}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Prioridad
              <select
                value={manualPriority}
                onChange={(event) => setManualPriority(event.target.value)}
              >
                <option value="low">Baja</option>
                <option value="normal">Normal</option>
                <option value="high">Alta</option>
                <option value="urgent">Urgente</option>
              </select>
            </label>

            <label className="workshop-manual-span-2">
              Motivo de ingreso / reporte del cliente
              <textarea
                rows={3}
                value={manualReport}
                onChange={(event) => setManualReport(event.target.value)}
              />
            </label>

            <label className="workshop-manual-span-2">
              Servicio solicitado
              <textarea
                rows={3}
                value={manualService}
                onChange={(event) => setManualService(event.target.value)}
              />
            </label>

            <label>
              Condición inicial
              <textarea
                rows={3}
                value={manualCondition}
                onChange={(event) => setManualCondition(event.target.value)}
              />
            </label>

            <label>
              Accesorios recibidos
              <textarea
                rows={3}
                value={manualAccessories}
                onChange={(event) => setManualAccessories(event.target.value)}
              />
            </label>

            <label className="workshop-manual-span-2">
              Observaciones
              <textarea
                rows={3}
                value={manualNotes}
                onChange={(event) => setManualNotes(event.target.value)}
              />
            </label>

            <div className="form-actions workshop-manual-span-2">
              <button
                className="primary-button"
                type="submit"
                disabled={manualBusy || customers.length === 0}
              >
                {manualBusy ? "Creando…" : "Crear orden de servicio"}
              </button>
            </div>
          </form>
        </section>
      ) : null}

      {actionError ? (
        <div className="inline-alert" role="alert">
          {actionError}
        </div>
      ) : null}

      {actionSuccess ? (
        <div className="form-success workshop-message">
          {actionSuccess}
        </div>
      ) : null}

      {candidates.length > 0 ? (
        <section className="panel-card">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Pendientes</span>
              <h2>Cotizaciones aprobadas</h2>
            </div>
          </div>

          <div className="workshop-candidate-grid">
            {candidates.map((candidate) => (
              <article
                className="workshop-candidate"
                key={candidate.quotation_id}
              >
                <div>
                  <strong>{candidate.quotation_number}</strong>
                  <span>{candidate.customer_name}</span>
                </div>
                <div className="workshop-candidate-action">
                  <strong>
                    {money(candidate.total, candidate.currency_code)}
                  </strong>
                  <button
                    className="primary-button"
                    type="button"
                    disabled={busyId === candidate.quotation_id}
                    onClick={() => void createOrder(candidate)}
                  >
                    {busyId === candidate.quotation_id
                      ? "Creando…"
                      : "Crear orden"}
                  </button>
                </div>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      <section className="toolbar-card">
        <div>
          <strong>{loading ? "…" : orders.length}</strong>
          <span> órdenes · {openOrders} abiertas</span>
        </div>
        <input
          type="search"
          placeholder="Buscar por orden, cliente o cotización"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </section>

      {loadError ? (
        <div className="inline-alert" role="alert">
          {loadError}
        </div>
      ) : null}

      {!loading && orders.length === 0 && !loadError ? (
        <section className="empty-state">
          <strong>No hay órdenes de servicio.</strong>
          <p>Crea una orden manual o conviértela desde una cotización aprobada.</p>
        </section>
      ) : null}

      <section className="workshop-grid" aria-label="Órdenes de servicio">
        {orders.map((order) => (
          <article className="workshop-card" key={order.id}>
            <div className="workshop-card-heading">
              <div>
                <span className={`status-badge service-status-${order.status}`}>
                  {STATUS_LABELS[order.status]}
                </span>
                <h2>{order.order_number}</h2>
                <p>{order.customer_name}</p>
              </div>
              <span className="priority-badge">{order.priority}</span>
            </div>

            <dl className="customer-meta">
              <div>
                <dt>Cotización</dt>
                <dd>{order.quotation_number ?? "—"}</dd>
              </div>
              <div>
                <dt>Aprobado</dt>
                <dd>{money(order.approved_total)}</dd>
              </div>
              <div>
                <dt>Solicitud</dt>
                <dd>{order.requested_service ?? "—"}</dd>
              </div>
              <div>
                <dt>Ingreso</dt>
                <dd>{new Date(order.received_at).toLocaleDateString("es-PE")}</dd>
              </div>
            </dl>

            <div className="quotation-actions">
              <Link className="compact-button" to={`/taller/${order.id}`}>
                Abrir detalle
              </Link>

              {statusActions(order.status).map((action) => (
                <button
                  key={action.value}
                  type="button"
                  className={
                    action.danger
                      ? "compact-button compact-button-danger"
                      : "compact-button"
                  }
                  disabled={busyId === order.id}
                  onClick={() => void changeStatus(order.id, action.value)}
                >
                  {busyId === order.id ? "Actualizando…" : action.label}
                </button>
              ))}
            </div>
          </article>
        ))}
      </section>
    </div>
  );
}
